"use client";

import { Search, X } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useFormStatus } from "react-dom";
import { Suspense, useEffect, useRef, useState, type ChangeEvent } from "react";

import {
  createCatalogSearchSubmittedEvent,
  trackCatalogEvent,
} from "@/components/catalog/analytics";
import { catalogUiContent } from "@/data/catalog-ui-content";

export interface CatalogSearchFormProps {
  readonly className?: string;
  readonly defaultValue?: string;
  readonly placeholder?: string;
}

export function CatalogSearchForm({
  className = "mt-4",
  defaultValue = "",
  placeholder = catalogUiContent.searchPlaceholder,
}: Readonly<CatalogSearchFormProps>) {
  return (
    <Suspense
      fallback={
        <CatalogSearchFormFallback
          className={className}
          placeholder={placeholder}
        />
      }
    >
      <CatalogSearchFormInner
        className={className}
        defaultValue={defaultValue}
        placeholder={placeholder}
      />
    </Suspense>
  );
}

function CatalogSearchFormInner({
  className = "mt-4",
  defaultValue = "",
  placeholder = catalogUiContent.searchPlaceholder,
}: Readonly<CatalogSearchFormProps>) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastSubmittedValue = useRef(defaultValue.trim());

  useEffect(() => {
    const nextValue = value.trim();

    if (pathname !== "/catalogo" || nextValue === lastSubmittedValue.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const params = new URLSearchParams(searchParams);

      if (nextValue) {
        params.set("q", nextValue);
      } else {
        params.delete("q");
      }

      lastSubmittedValue.current = nextValue;
      router.replace(params.size > 0 ? `/catalogo?${params.toString()}` : "/catalogo");
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [pathname, router, searchParams, value]);

  return (
    <form
      action="/catalogo"
      className={`${className}`}
      onSubmit={() => {
        const submittedValue = value.trim();
        const event = createCatalogSearchSubmittedEvent(submittedValue);

        lastSubmittedValue.current = submittedValue;

        if (event) {
          trackCatalogEvent(event, { keepalive: true });
        }
      }}
    >
      <div className="flex h-12 w-full items-center gap-1 rounded-xl border border-outline-variant bg-surface pl-4 pr-1 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <input
          ref={inputRef}
          className="h-full min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-outline-variant [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          name="q"
          placeholder={placeholder}
          type="search"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        />
        {value ? (
          <button
            aria-label="Limpiar búsqueda"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-outline outline-none hover:bg-primary/5 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
            onClick={() => {
              setValue("");
              inputRef.current?.focus();
            }}
            type="button"
          >
            <X aria-hidden="true" size={18} strokeWidth={2} />
          </button>
        ) : null}
        <SearchSubmitButton />
      </div>
    </form>
  );
}

function CatalogSearchFormFallback({
  className,
  placeholder,
}: Readonly<{
  className: string;
  placeholder: string;
}>) {
  return (
    <form action="/catalogo" className={className}>
      <div className="flex h-12 w-full items-center gap-1 rounded-xl border border-outline-variant bg-surface pl-4 pr-1">
        <input
          className="h-full min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-outline-variant"
          name="q"
          placeholder={placeholder}
          type="search"
        />
        <button
          aria-label="Buscar"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary"
          type="submit"
        >
          <Search aria-hidden="true" size={20} strokeWidth={2} />
        </button>
      </div>
    </form>
  );
}

function SearchSubmitButton() {
  const { pending } = useFormStatus();

  return (
    <button
      aria-label="Buscar"
      aria-busy={pending}
      className={`inline-flex h-11 shrink-0 items-center justify-center rounded-lg text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary ${
        pending ? "w-auto px-3 text-sm font-extrabold" : "w-10"
      }`}
      disabled={pending}
      type="submit"
    >
      {pending ? "Buscando..." : <Search aria-hidden="true" size={20} strokeWidth={2} />}
    </button>
  );
}
